import React, { useState } from 'react';
import { Building, Calendar, Users, Phone, CheckCircle, X, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import KanbanBoard from '../shared/KanbanBoard';
import ProspectDetailByWidgetModal from './ProspectDetailByWidgetModal';
import { useData } from '../../hooks/useData';

interface WidgetProspect {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  requestedPromoter: string;
  requestedDate: string;
  requestedTime: string;
  requestedOptions: Array<{date: string, time: string}>;
  submissionDate: string;
  status: 'pending_validation' | 'approved' | 'rejected' | 'assigned' | 'completed';
  widget: string;
  notes?: string;
  assignedPromoters?: string[];
}

const ProspectsWidgetSubmissions: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { promoters } = useData();
  const [selectedProspect, setSelectedProspect] = useState<WidgetProspect | null>(null);

  // Mock data for widget submissions
  const [widgetProspects, setWidgetProspects] = useState<WidgetProspect[]>([
    {
      id: 'w1',
      name: 'Jennifer Martinez',
      email: 'jennifer.martinez@techcorp.com',
      phone: '+1 555 123 4567',
      company: 'TechCorp Solutions',
      requestedPromoter: 'Marie Leroy',
      requestedDate: '2024-02-05',
      requestedTime: '14:00',
      submissionDate: '2024-01-28',
      status: 'pending_validation',
      widget: 'Homepage Widget',
      notes: 'Interested in Clareefai Platform for marketing automation'
    },
    {
      id: 'w2',
      name: 'Robert Chen',
      email: 'robert.chen@startup.io',
      phone: '+1 555 987 6543',
      company: 'Startup Innovations',
      requestedPromoter: 'Sophie Durand',
      requestedDate: '2024-02-08',
      requestedTime: '10:30',
      submissionDate: '2024-01-30',
      status: 'approved',
      widget: 'Product Page Widget',
      assignedPromoters: ['Sophie Durand', 'Thomas Bernard']
    },
    {
      id: 'w3',
      name: 'Lisa Thompson',
      email: 'lisa.thompson@enterprise.com',
      phone: '+1 555 456 7890',
      company: 'Enterprise Solutions',
      requestedPromoter: 'Pierre Martin',
      requestedDate: '2024-02-10',
      requestedTime: '16:00',
      submissionDate: '2024-02-01',
      status: 'assigned',
      widget: 'Landing Page Widget',
      assignedPromoters: ['Pierre Martin', 'Marie Leroy']
    },
    {
      id: 'w4',
      name: 'Michael Davis',
      email: 'michael.davis@consulting.fr',
      phone: '+33 1 23 45 67 89',
      company: 'Business Consulting Pro',
      requestedPromoter: 'Antoine Moreau',
      requestedDate: '2024-02-12',
      requestedTime: '11:00',
      submissionDate: '2024-02-03',
      status: 'completed',
      widget: 'Blog Widget',
      assignedPromoters: ['Antoine Moreau']
    },
    {
      id: 'w5',
      name: 'Sarah Wilson',
      email: 'sarah.wilson@healthtech.com',
      phone: '+1 555 321 0987',
      company: 'HealthTech Innovations',
      requestedPromoter: 'Isabelle Petit',
      requestedDate: '2024-02-15',
      requestedTime: '09:30',
      submissionDate: '2024-02-05',
      status: 'rejected',
      widget: 'Case Study Widget',
      notes: 'Not qualified for our target market'
    }
  ]);

  const columns = [
    {
      id: 'pending_validation',
      title: t('prospects.widgetStatus.pendingValidation'),
      items: widgetProspects.filter(p => p.status === 'pending_validation'),
      color: 'bg-yellow-100 text-yellow-800',
      headerColor: 'bg-gradient-to-r from-yellow-500 to-orange-500',
      borderColor: '#eab308'
    },
    {
      id: 'approved',
      title: t('prospects.widgetStatus.approved'),
      items: widgetProspects.filter(p => p.status === 'approved'),
      color: 'bg-blue-100 text-blue-800',
      headerColor: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      borderColor: '#3b82f6'
    },
    {
      id: 'assigned',
      title: t('prospects.widgetStatus.assignedToPromoters'),
      items: widgetProspects.filter(p => p.status === 'assigned'),
      color: 'bg-green-100 text-green-800',
      headerColor: 'bg-gradient-to-r from-green-500 to-emerald-500',
      borderColor: '#10b981'
    },
    {
      id: 'completed',
      title: t('prospects.widgetStatus.callCompleted'),
      items: widgetProspects.filter(p => p.status === 'completed'),
      color: 'bg-purple-100 text-purple-800',
      headerColor: 'bg-gradient-to-r from-purple-500 to-violet-500',
      borderColor: '#8b5cf6'
    },
    {
      id: 'rejected',
      title: t('prospects.widgetStatus.rejected'),
      items: widgetProspects.filter(p => p.status === 'rejected'),
      color: 'bg-red-100 text-red-800',
      headerColor: 'bg-gradient-to-r from-red-500 to-pink-500',
      borderColor: '#ef4444'
    }
  ];

  const handleProspectClick = (prospect: WidgetProspect) => {
    setSelectedProspect(prospect);
  };

  const handleUpdateProspect = (prospectId: string, updates: Partial<WidgetProspect>) => {
    setWidgetProspects(prev => prev.map(prospect => 
      prospect.id === prospectId ? { ...prospect, ...updates } : prospect
    ));
  };

  const renderCard = (prospect: WidgetProspect) => (
    <div 
      className="space-y-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
      onClick={() => handleProspectClick(prospect)}
    >
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">{prospect.name}</h4>
      </div>
      
      <div className="text-sm text-gray-600 space-y-1">
        <p className="flex items-center">
          <Building className="w-4 h-4 mr-2" />
          {prospect.company}
        </p>
        <p className="flex items-center">
          <Phone className="w-4 h-4 mr-2" />
          {t('prospects.widget.requestedPromoter')} {prospect.requestedPromoter}
        </p>
        <p className="flex items-center">
          <Calendar className="w-4 h-4 mr-2" />
          {new Date(prospect.requestedDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')} {t('prospects.at')} {prospect.requestedTime}
        </p>
      </div>

      <div className="pt-2 border-t">
        <div className="text-xs text-gray-500 mb-2">
          {t('prospects.widget.widgetLabel')}: {prospect.widget}
        </div>
        <div className="text-xs text-gray-500 mb-2">
          {t('prospects.widget.submitted')}: {new Date(prospect.submissionDate).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
        </div>
        <span className={`status-badge ${
          prospect.status === 'pending_validation' ? 'status-invited' :
          prospect.status === 'approved' ? 'status-review' :
          prospect.status === 'assigned' ? 'status-available' :
          prospect.status === 'completed' ? 'status-completed' :
          'status-failed'
        }`}>
          {prospect.status === 'pending_validation' ? t('prospects.widgetStatus.pendingValidation') :
           prospect.status === 'approved' ? t('prospects.widgetStatus.approved') :
           prospect.status === 'assigned' ? t('prospects.widgetStatus.assigned') :
           prospect.status === 'completed' ? t('prospects.widgetStatus.completed') :
           t('prospects.widgetStatus.rejected')}
        </span>
      </div>
    </div>
  );

  return (
    <>
      <KanbanBoard
        columns={columns}
        renderCard={renderCard}
      />

      {selectedProspect && (
        <ProspectDetailByWidgetModal
          prospect={selectedProspect}
          isOpen={!!selectedProspect}
          onClose={() => setSelectedProspect(null)}
          onUpdate={handleUpdateProspect}
        />
      )}
    </>
  );
};

export default ProspectsWidgetSubmissions;